import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { zodValidator } from "@tanstack/zod-adapter";
import { visagistThemes, type Visagist, type VisagistTheme } from "@/data/visagists";
import { priceBuckets } from "@/data/photographers";
import { fetchAllVisagists } from "@/lib/visagists-source";
import { PortraitImage } from "@/components/PortraitImage";
import { CreatorFilterBar } from "@/components/CreatorFilterBar";
import { PhotoLightbox, type LightboxInfo } from "@/components/PhotoLightbox";
import { creatorFiltersSchema, matchesCreatorFilters, type CreatorFilters } from "@/lib/creator-filters";

type ThemeData = (typeof visagistThemes)[number];

const ALL_VISAGIST_THEME: ThemeData = {
  id: "all" as VisagistTheme,
  label: "Alle",
  description: "Alle Arbeiten aus allen Make-Up-Kategorien, die komplette Auswahl.",
};

/** Keyword-rich, indexable SEO copy per Visagisten-Theme. */
const VISAGIST_SEO: Record<string, { title: string; description: string }> = {
  all: {
    title: "Visagisten & Make-up Artists finden | OM Selected",
    description:
      "Kuratierte Visagisten und Make-up Artists für Tages-, Abend-, Braut- und Editorial-Make-up. Buche professionelle Maskenbildner in deiner Region bei OM Selected.",
  },
  "day-makeup": {
    title: "Tages Make-up Artist buchen | Natürliche Looks | OM Selected",
    description:
      "Visagisten für natürliches Tages-Make-up: frische, alltagstaugliche Looks für Business, Bewerbungsfotos und Portraits. Geprüfte Make-up Artists bei OM Selected buchen.",
  },
  "evening-makeup": {
    title: "Abend Make-up Artist buchen | Glamour & Event | OM Selected",
    description:
      "Glamouröses Abend-Make-up für Galas, Events und Foto-Shootings. Finde professionelle Visagisten und Make-up Artists für deinen Auftritt bei OM Selected.",
  },
  bridal: {
    title: "Braut Make-up Artist & Brautstyling buchen | OM Selected",
    description:
      "Brautstyling, Make-up und Hair für den schönsten Tag. Kuratierte Braut-Visagisten und Make-up Artists für deine Hochzeit, jetzt bei OM Selected entdecken.",
  },
  editorial: {
    title: "Editorial & Fashion Make-up Artist buchen | OM Selected",
    description:
      "Editorial-, Fashion- und Kampagnen-Make-up von professionellen Visagisten. Finde Make-up Artists für Mode-Shootings und Kampagnen bei OM Selected.",
  },
  open: {
    title: "Make-up Artist & Visagist buchen | Freie Arbeiten | OM Selected",
    description:
      "Vielseitige Visagisten und Make-up Artists für alle Looks zwischen Tages-Make-up und Editorial. Entdecke kuratierte Maskenbildner bei OM Selected.",
  },
};

export const Route = createFileRoute("/visagists/themes/$theme")({
  validateSearch: zodValidator(creatorFiltersSchema),
  loader: ({ params }) => {
    if (params.theme === "all") return { theme: ALL_VISAGIST_THEME };
    const theme = visagistThemes.find((t) => t.id === params.theme);
    if (!theme) throw notFound();
    return { theme };
  },
  head: ({ loaderData, params }) => {
    const t = loaderData?.theme;
    const url = `https://omselected.art/visagists/themes/${params.theme}`;
    if (!t) return { meta: [{ title: "Visagist:innen, OM Selected" }] };
    const seo = VISAGIST_SEO[t.id as string] ?? {
      title: `${t.label} Make-up Artist buchen | OM Selected`,
      description: `${t.label}: ${t.description}`,
    };
    return {
      meta: [
        { title: seo.title },
        { name: "description", content: seo.description },
        { property: "og:title", content: seo.title },
        { property: "og:description", content: seo.description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "OM Selected" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: seo.title },
        { name: "twitter:description", content: seo.description },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: seo.title,
            description: seo.description,
            url,
            isPartOf: { "@type": "WebSite", name: "OM Selected", url: "https://omselected.art" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "OM Selected", item: "https://omselected.art" },
              { "@type": "ListItem", position: 2, name: "Visagisten", item: "https://omselected.art/visagists" },
              { "@type": "ListItem", position: 3, name: t.label, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => {
    const { t } = useTranslation();
    return (
      <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
        <h1 className="serif text-4xl">{t("theme.notFound")}</h1>
        <Link to="/visagists" className="eyebrow inline-block mt-8 border-b border-foreground pb-1">{t("theme.allVisagists")}</Link>
      </div>
    );
  },
  errorComponent: ({ error }) => {
    const { t } = useTranslation();
    return (
      <div className="mx-auto max-w-[800px] px-5 py-32 text-center">
        <h1 className="serif text-4xl">{t("profile.error")}</h1>
        <p className="mt-4 text-muted-foreground">{error.message}</p>
      </div>
    );
  },
  component: ThemePage,
});

function ThemePage() {
  const { t: tr } = useTranslation();
  const { theme } = Route.useLoaderData() as { theme: ThemeData };
  const isAll = theme.id === ("all" as VisagistTheme);
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [all, setAll] = useState<Visagist[]>([]);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const PAGE_SIZE = 12;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [landscape, setLandscape] = useState<Record<string, boolean>>({});

  const updateFilters = (patch: Partial<CreatorFilters>) =>
    navigate({ search: (prev: CreatorFilters) => ({ ...prev, ...patch }), replace: true });
  const resetFilters = () =>
    navigate({
      search: { country: "", city: "", theme: "", price: "", setting: "", language: "", page: 1 },
      replace: true,
    });

  useEffect(() => {
    let active = true;
    fetchAllVisagists().then((data) => {
      if (active) {
        setAll(data);
        setLoading(false);
      }
    });
    return () => { active = false; };
  }, []);

  const themeMatches = isAll
    ? all
    : all.filter((p) => p.themes.includes(theme.id as VisagistTheme));
  const matching = themeMatches.filter((p) =>
    matchesCreatorFilters(p, search, priceBuckets),
  );
  const cityOptions = Array.from(
    new Set(
      themeMatches
        .map((p) => p.city ?? p.region)
        .filter((c): c is string => !!c && c !== ""),
    ),
  ).sort();

  const works = useMemo(() => {
    const items = all.flatMap((p) =>
      (p.gallery ?? [])
        .map((src, i) => {
          const slotTheme = (p.galleryThemes?.[i] ?? "open") as VisagistTheme;
          const label = visagistThemes.find((th) => th.id === slotTheme)?.label ?? "";
          return {
            id: `${p.id}-${i}`,
            image: src,
            name: p.name,
            region: p.city ?? p.region,
            theme: slotTheme,
            themeLabel: label,
            visagistId: p.id,
            instagramHandle: p.instagramHandle ?? null,
            websiteUrl: p.websiteUrl ?? null,
          };
        })
        .filter((w) => isAll || w.theme === (theme.id as VisagistTheme)),
    );
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  }, [all, isAll, theme.id]);
  const visibleWorks = works.slice(0, visibleCount);
  const hasMore = visibleCount < works.length;
  const makeInfo = (w: (typeof works)[number]): LightboxInfo => ({
    image: w.image,
    caption: w.name,
    photographerName: w.name,
    photographerId: w.visagistId,
    region: w.region,
    theme: w.themeLabel,
    instagramHandle: w.instagramHandle,
    websiteUrl: w.websiteUrl,
    creatorType: "visagist",
  });
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [theme.id]);

  return (
    <div>
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-12 pb-16">
          <div className="flex gap-6 eyebrow mb-8">
            <Link
              to="/themes/$theme"
              params={{ theme: "portraits" }}
              className="text-muted-foreground hover:text-foreground"
            >
              {tr("theme.creatorTypePhotographers")}
            </Link>
            <Link
              to="/visagists/themes/$theme"
              params={{ theme: "day-makeup" }}
              className="text-foreground border-b border-foreground pb-1"
            >
              {tr("theme.creatorTypeVisagists")}
            </Link>
          </div>
          <div className="eyebrow text-muted-foreground">{tr("theme.visagistEyebrow")}</div>
          <h1 className="serif text-6xl md:text-8xl mt-6 leading-[0.9]">{theme.label}</h1>
          <p className="serif italic text-xl md:text-2xl mt-8 max-w-xl">{theme.description}</p>
          <div className="mt-10 flex flex-wrap gap-4 eyebrow text-muted-foreground">
            <Link
              to="/visagists/themes/$theme"
              params={{ theme: "all" }}
              className={isAll ? "text-foreground border-b border-foreground pb-1" : "hover:text-foreground"}
            >
              {tr("theme.all")}
            </Link>
            {visagistThemes.map((t) => (
              <Link
                key={t.id}
                to="/visagists/themes/$theme"
                params={{ theme: t.id }}
                className={t.id === theme.id ? "text-foreground border-b border-foreground pb-1" : "hover:text-foreground"}
              >
                {t.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {visibleWorks.length > 0 && (
        <section className="border-b border-border">
          <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
            <h2 className="eyebrow text-muted-foreground mb-8 font-normal">{tr("theme.selected")}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 [grid-auto-flow:dense]">
              {visibleWorks.map((w, i) => (
                <button
                  key={w.id}
                  type="button"
                  onClick={() => setLightboxIndex(i)}
                  className={`block group text-left w-full ${landscape[w.id] ? "col-span-2" : ""}`}
                >
                  <figure>
                    <img
                      src={w.image}
                      alt={`${theme.label} Make-Up von ${w.name}`}
                      loading="lazy"
                      onLoad={(e) => {
                        const img = e.currentTarget;
                        if (img.naturalWidth > img.naturalHeight * 1.1) {
                          setLandscape((prev) => (prev[w.id] ? prev : { ...prev, [w.id]: true }));
                        }
                      }}
                      className="w-full h-auto cursor-zoom-in bg-muted"
                    />
                    <figcaption className="mt-2 text-xs italic text-muted-foreground group-hover:text-foreground">{w.name}</figcaption>
                  </figure>
                </button>
              ))}
            </div>
            {hasMore && (
              <div className="mt-12 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="eyebrow border-b border-foreground pb-1 hover:opacity-70 transition-opacity"
                >
                  {tr("theme.loadMore", { count: works.length - visibleCount })}
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      <section>
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
          <h2 className="eyebrow text-muted-foreground mb-8 font-normal">{tr("theme.visagistsInCategory")}</h2>
          <div className="border-y border-border bg-secondary/40 -mx-5 md:-mx-10 mb-10">
            <CreatorFilterBar
              values={search}
              onChange={updateFilters}
              onReset={resetFilters}
              resultsCount={matching.length}
              show={{
                city: cityOptions.map((c) => ({ value: c, label: c })),
                price: priceBuckets.map((b) => ({ value: b.id, label: b.label })),
                setting: true,
                language: true,
              }}
            />
          </div>
          {loading ? (
            <div className="text-xs text-muted-foreground italic">{tr("common.loading")}</div>
          ) : matching.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">{tr("theme.emptyVisagists")}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
              {matching.map((p) => (
                <Link key={p.id} to="/visagists/$id" params={{ id: p.id }} className="block">
                  <PortraitImage p={p} />
                  <div className="serif text-xl mt-4">{p.name}</div>
                  <div className="eyebrow text-muted-foreground mt-1">{p.region}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <PhotoLightbox
        info={lightboxIndex !== null && visibleWorks[lightboxIndex] ? makeInfo(visibleWorks[lightboxIndex]) : null}
        onClose={() => setLightboxIndex(null)}
        onPrev={lightboxIndex !== null && lightboxIndex > 0 ? () => setLightboxIndex((i) => (i ?? 0) - 1) : undefined}
        onNext={lightboxIndex !== null && lightboxIndex < visibleWorks.length - 1 ? () => setLightboxIndex((i) => (i ?? 0) + 1) : undefined}
      />
    </div>
  );
}